/** MOMAX Header V3 — measure on form/size changes, not during scroll. */
(() => {
  'use strict';
  window.MomaxHeader = {setForm, getForm: () => header?.dataset.form || null};

  const selector = '[data-momax-header]';
  const desktop = window.matchMedia('(min-width: 1200px)');
  let header;
  let main;
  let resizeObserver;
  let contentObserver;
  let secondSectionObserver;
  let legacyHero;
  let animationFrame = 0;
  let measureFrame = 0;
  let scrollFrame = 0;
  let lastWidth = -1;
  let lastMeasuredForm = null;
  let lastHeaderHeight = -1;
  let lastMainHeight = -1;

  function syncTopbar() {
    const topbar = header?.querySelector('[data-momax-topbar]');
    if (topbar) topbar.inert = header.dataset.form !== 'compact' ||
      header.hasAttribute('data-topbar-dismissed');
  }

  function dismissTopbar(restoreFocus = false) {
    if (!header) return;
    header.setAttribute('data-topbar-dismissed', '');
    syncTopbar();
    if (restoreFocus) header.querySelector('.header__logo-link')?.focus({preventScroll: true});
  }

  function measure(force = false) {
    if (!header?.isConnected) return;
    const width = header.clientWidth;
    const form = header.dataset.form;
    if (!force && width === lastWidth && form === lastMeasuredForm) return;
    lastWidth = width;
    lastMeasuredForm = form;

    header.style.setProperty('--momax-header-menu-width', `${width}px`);
    if (!desktop.matches) {
      header.removeAttribute('data-nav-dense');
      header.removeAttribute('data-overflow');
      return;
    }

    const wrapper = header.querySelector('.momax-header__wrapper');
    const panel = header.querySelector('.momax-header__panel');
    const navigation = header.querySelector('.momax-header__navigation');
    const menuButton = header.querySelector('.momax-header__menu');
    if (!wrapper || !panel) return;
    const activeElement = document.activeElement;

    // Measure the expanded layout every time. A collapsed navigation has zero
    // width and would otherwise be incorrectly expanded on the next resize.
    header.removeAttribute('data-overflow');
    header.removeAttribute('data-nav-dense');
    // Let compact mode use its maximum CSS-constrained width while measuring,
    // then size it to the chosen layout, including the actual inner insets.
    header.style.setProperty('--momax-header-compact-width', `${width}px`);
    const wrapperStyle = getComputedStyle(wrapper);
    const available = wrapper.clientWidth -
      (parseFloat(wrapperStyle.paddingLeft) || 0) -
      (parseFloat(wrapperStyle.paddingRight) || 0);
    const panelInset = panel.getBoundingClientRect().width - available;
    const requiredWidth = () => {
      const items = Array.from(wrapper.children).filter(item => getComputedStyle(item).display !== 'none');
      return items.reduce((total, item) => total + item.getBoundingClientRect().width, 0) +
        (parseFloat(getComputedStyle(wrapper).columnGap) || 0) * Math.max(0, items.length - 1);
    };
    let contentWidth = requiredWidth();
    if (contentWidth > available) {
      header.setAttribute('data-nav-dense', '');
      contentWidth = requiredWidth();
    }
    const overflow = contentWidth > available;
    header.toggleAttribute('data-overflow', overflow);
    if (overflow) contentWidth = requiredWidth();
    header.style.setProperty('--momax-header-compact-width',
      `${Math.max(998, Math.ceil(contentWidth + panelInset))}px`);

    if (overflow && navigation?.contains(activeElement)) {
      menuButton?.focus({preventScroll: true});
    } else if (!overflow && activeElement === menuButton) {
      navigation?.querySelector('a')?.focus({preventScroll: true});
    }
  }

  function scheduleMeasure(force = false) {
    if (force) {
      lastWidth = -1;
      lastMeasuredForm = null;
    }
    if (measureFrame) return;
    measureFrame = requestAnimationFrame(() => {
      measureFrame = 0;
      measure();
    });
  }

  function setForm(form, {animate = true} = {}) {
    if (!['full', 'compact', 'hidden'].includes(form)) {
      throw new TypeError('Header form must be full, compact, or hidden.');
    }
    if (!header?.isConnected) header = document.querySelector(selector);
    if (!header) return false;
    if (header.dataset.form === form) return true;

    if (animationFrame) cancelAnimationFrame(animationFrame);
    header.toggleAttribute('data-no-animation', !animate);
    header.dataset.form = form;
    // Header configuration is an infrequent discrete state change; measure
    // immediately here but never on each normal scroll frame.
    measure(true);
    syncTopbar();
    if (!animate) {
      // Removing the no-animation flag on the next frame avoids a forced
      // offsetWidth read solely to flush CSS transitions.
      animationFrame = requestAnimationFrame(() => {
        animationFrame = 0;
        if (header?.isConnected) header.removeAttribute('data-no-animation');
      });
    }
    return true;
  }

  function isSecondModuleVisible() {
    if (!legacyHero) return true;
    const third = document.querySelector('.mm-waitlist, mm-waitlist') ||
      document.querySelector('.momax-lineup, momax-lineup')?.closest('.shopify-section')?.nextElementSibling;
    if (!third) {
      const second = document.querySelector('.momax-lineup, momax-lineup');
      if (second) return second.getBoundingClientRect().bottom <= innerHeight;
      return scrollY > (legacyHero.offsetHeight || innerHeight);
    }
    const rect = third.getBoundingClientRect();
    return header?.dataset.form === 'compact'
      ? rect.top <= innerHeight + 100 : rect.top <= innerHeight;
  }

  function syncHero(animate = false) {
    const homeBanner = document.querySelector('momax-home-banner');
    if (homeBanner) {
      const form = homeBanner.dataset.headerForm ||
        (homeBanner.dataset.state === 'intro' ? 'full' : 'compact');
      if (header?.dataset.form !== form) setForm(form, {animate});
      return;
    }
    if (!legacyHero) {
      const form = header?.dataset.defaultForm || 'full';
      if (header?.dataset.form !== form) setForm(form, {animate});
      return;
    }
    const form = isSecondModuleVisible() ? 'compact' : 'hidden';
    if (header?.dataset.form !== form) setForm(form, {animate});
  }

  function observeSecondModule() {
    secondSectionObserver?.disconnect();
    legacyHero = document.querySelector('mm-hero');
    if (!legacyHero) return;
    const third = document.querySelector('.mm-waitlist, mm-waitlist') ||
      document.querySelector('.momax-lineup, momax-lineup')?.closest('.shopify-section')?.nextElementSibling;
    const target = third || document.querySelector('.momax-lineup, momax-lineup');
    if (!target) return;
    secondSectionObserver = new IntersectionObserver(() => syncHero(true),
      {threshold: [0, 0.25, 0.5, 0.75, 1]});
    secondSectionObserver.observe(target);
  }

  function blockHeight(entry) {
    const size = entry.borderBoxSize;
    if (size) {
      const box = Array.isArray(size) ? size[0] : size;
      if (box && Number.isFinite(box.blockSize)) return box.blockSize;
    }
    return entry.target.getBoundingClientRect().height;
  }

  function updateHeights(entries) {
    for (const entry of entries) {
      const height = blockHeight(entry);
      if (entry.target === header && Math.abs(height - lastHeaderHeight) > 0.5) {
        lastHeaderHeight = height;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
      if (entry.target === main && Math.abs(height - lastMainHeight) > 0.5) {
        lastMainHeight = height;
        document.documentElement.style.setProperty('--momax-header-overlay-height', `${height}px`);
      }
    }
  }

  function connect() {
    resizeObserver?.disconnect();
    contentObserver?.disconnect();
    secondSectionObserver?.disconnect();
    if (measureFrame) cancelAnimationFrame(measureFrame);
    measureFrame = 0;
    header = document.querySelector(selector);
    main = header?.querySelector('.momax-header__main') || null;
    if (!header) return;
    lastWidth = -1;
    lastMeasuredForm = null;
    lastHeaderHeight = -1;
    lastMainHeight = -1;
    legacyHero = document.querySelector('mm-hero');

    measure(true);
    syncHero();
    syncTopbar();
    observeSecondModule();
    resizeObserver = new ResizeObserver(entries => {
      updateHeights(entries);
      // The header may change width after editor updates or responsive CSS.
      // Do not observe documentElement: its height changes on scroll/content
      // updates and caused unnecessary full menu remeasurements.
      if (entries.some(entry => entry.target === header)) scheduleMeasure();
    });
    resizeObserver.observe(header, {box: 'border-box'});
    if (main) resizeObserver.observe(main, {box: 'border-box'});
    const wrapper = header.querySelector('.momax-header__wrapper');
    if (wrapper) {
      // Menu/localization edits can change the required width without resizing
      // the header. Ignore attributes changed by measurement and dropdowns.
      contentObserver = new MutationObserver(() => scheduleMeasure(true));
      contentObserver.observe(wrapper, {childList: true, characterData: true, subtree: true});
    }
  }

  let scrollTicking = false;
  window.addEventListener('scroll', () => {
    // On the current homepage, momax-home-banner.js owns header state.
    // Only the legacy <mm-hero> design needs per-scroll visibility checks.
    if (!legacyHero || scrollTicking) return;
    scrollTicking = true;
    scrollFrame = requestAnimationFrame(() => {
      scrollTicking = false;
      scrollFrame = 0;
      syncHero(true);
    });
  }, {passive: true});

  document.addEventListener('mm-hero:state-change', event => {
    if (event.target === legacyHero) syncHero(event.detail?.animate ?? true);
  });
  document.addEventListener('shopify:section:load', connect);
  document.addEventListener('shopify:section:unload', () => queueMicrotask(connect));
  desktop.addEventListener('change', () => {
    scheduleMeasure(true);
    syncTopbar();
    syncHero();
    observeSecondModule();
  });
  document.addEventListener('momax:topbar-dismiss', () => dismissTopbar());
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-momax-region]')) {
      document.dispatchEvent(new CustomEvent('open::countrySelector'));
    }
    if (event.target.closest('[data-momax-topbar-close]') && header) dismissTopbar(true);
  });
  window.addEventListener('resize', () => {
    scheduleMeasure();
    if (legacyHero) syncHero();
  }, {passive: true});

  connect();
  document.fonts?.ready.then(() => scheduleMeasure(true));
  document.fonts?.addEventListener('loadingdone', () => scheduleMeasure(true));
})();
